import { getCurrentOperatorName } from '../../utils/currentOperator';
import React from 'react';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  EconomicsPreview,
  ScopePill,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import { Building2, CalendarDays, Landmark, MapPin, TrendingUp, Users } from 'lucide-react';

import { DateField } from '../shared/DateField';

const fmtAmount = (value: string) => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};

const pct = (value: number | null, digits = 1) =>
  value == null || !Number.isFinite(value) ? '-' : `${value.toFixed(digits)}%`;

const ratio = (value: number | null, digits = 2) =>
  value == null || !Number.isFinite(value) ? '-' : `${value.toFixed(digits)}x`;

const RealEstateEquityTicketScreen: React.FC = () => {
  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const initialFormData = {
    // RE Equity ticket is acquisition-only; dispose flows from the position dashboard.
    transactionType: 'BUY' as const,
    assetManager: pendingTrade?.assetManager || '',
    portfolioManager: pendingTrade?.portfolioManager || '',
    assetClass: 'Real Estate',
    assetSubClass: 'RE Equity - Direct',
    security: pendingTrade?.security || pendingTrade?.securityName || '',
    identifier: pendingTrade?.identifier || pendingTrade?.cusip || '',
    portfolio: pendingTrade?.portfolio || pendingTrade?.portfolioName || '',
    amount: pendingTrade?.amount || pendingTrade?.equityInvested || '',
    tradeDate: pendingTrade?.tradeDate || getTodayDate(),
    // RE equity settles on closing/acquisition date — no T+N convention.
    // Default settle = trade so the fact_positions bi-temporal anchor matches
    // every follow-on lifecycle event (MTM/lease/dispose).
    settleDate: pendingTrade?.settleDate || pendingTrade?.tradeDate || getTodayDate(),
    submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
    currency: pendingTrade?.currency || 'USD',
    executedBy: pendingTrade?.executedBy || '',
    notes: pendingTrade?.notes || '',
    // RE Equity specific
    propertyName: pendingTrade?.propertyName || pendingTrade?.securityName || '',
    propertyType: pendingTrade?.propertyType || '',
    location: pendingTrade?.location || '',
    equityInvested: pendingTrade?.equityInvested || pendingTrade?.amount || '',
    ownershipPct: pendingTrade?.ownershipPct || '1',
    acquisitionDate: pendingTrade?.acquisitionDate || getTodayDate(),
    expectedHoldYears: pendingTrade?.expectedHoldYears || '',
    currentValuation: pendingTrade?.currentValuation || pendingTrade?.propertyValuation || '',
    capRate: pendingTrade?.capRate ? (parseFloat(pendingTrade.capRate) * 100).toString() : '',
    currentNoi: pendingTrade?.currentNoi || pendingTrade?.netOperatingIncome || '',
    occupancyPct: pendingTrade?.occupancyPct || '',
    valuationDate: pendingTrade?.valuationDate || pendingTrade?.appraisalDate || getTodayDate(),
    valuationMethod: pendingTrade?.valuationMethod || 'CAP_RATE',
    appraisalFirm: pendingTrade?.appraisalFirm || '',
    totalDebt: pendingTrade?.totalDebt || pendingTrade?.loanAmount || '',
    annualDebtService: pendingTrade?.annualDebtService || '',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.propertyName?.trim()) errors.propertyName = 'Property Name is required';
    if (!formData.propertyType) errors.propertyType = 'Property Type is required';
    if (!formData.equityInvested) errors.equityInvested = 'Equity Invested is required';
    if (formData.equityInvested && parseAmountShorthand(formData.equityInvested) <= 0) errors.equityInvested = 'Must be > 0';
    if (!formData.currentValuation) errors.currentValuation = 'Current Valuation is required';
    if (formData.currentValuation && parseAmountShorthand(formData.currentValuation) <= 0) errors.currentValuation = 'Must be > 0';
    if (!formData.acquisitionDate) errors.acquisitionDate = 'Acquisition Date is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';

    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find(
      (p: any) => p.portfolioName === formData.portfolio.trim()
    );
    if (!portfolio) {
      throw new Error(`Portfolio "${formData.portfolio}" not found`);
    }

    const capRatePct = formData.capRate ? parseFloat(formData.capRate) : null;

    // Step 1 — synthesise the security only (no silo write at DRAFT). The
    // silo (fact_re_equity_position) is materialised by RealEstateEquityPostingSpi
    // when the ACQUIRE trade reaches POSTED, using the RE metadata columns
    // sent on the trade payload below.
    const preparePayload: Record<string, any> = {
      portfolioId: portfolio.id,
      propertyName: formData.propertyName.trim(),
      propertyType: formData.propertyType,
    };

    const prepareRes = await axios.post('/api/credit/re-equity/prepare', preparePayload);
    const { securityId, sourceRef } = prepareRes.data;

    const equityInvested = parseAmountShorthand(formData.equityInvested);
    const totalDebtNum = formData.totalDebt ? parseAmountShorthand(formData.totalDebt) : null;
    const valuationNum = formData.currentValuation ? parseAmountShorthand(formData.currentValuation) : null;
    const noiNum = formData.currentNoi ? parseAmountShorthand(formData.currentNoi) : null;
    const adsNum = formData.annualDebtService ? parseAmountShorthand(formData.annualDebtService) : null;

    // Resolve dropdown names → dimension ids (matches FixedIncomeTicketScreen pattern).
    let assetManagerId: number | null = null;
    if (formData.assetManager && formData.assetManager.trim()) {
      const am = dimensionData.assetManagers.find(
        (a: any) => a.assetManagerName === formData.assetManager.trim()
      );
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find(
        (p: any) => p.portfolioManagerName === formData.portfolioManager.trim()
      );
      if (pm?.id) portfolioManagerId = pm.id;
    }

    // Step 2 — full trade payload. All RE fields the silo will need at POST
    // ride along here. cap_rate is sent as a percentage (e.g. 5.50, not 0.055)
    // so the trade column (numeric(5,2)) keeps its precision; the SPI converts
    // to decimal when populating the silo.
    return {
      transactionType: formData.transactionType,
      securityId: securityId,
      portfolioId: portfolio.id,
      assetManagerId: assetManagerId,
      portfolioManagerId: portfolioManagerId,
      quantity: equityInvested,
      price: 100,
      amount: equityInvested,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || '',
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'RE',
      // Drives RealEstateEquityPostingSpi event routing at POST time.
      // ACQUIRE → builds fact_re_equity_position silo from trade fields, then runs
      // standard share-lot + cash + aggregation, then upserts fact_position_re_attrs.
      // Dispose runs from the position dashboard, not this ticket.
      eventType: 'ACQUIRE',
      sourceRef: sourceRef,
      currency: formData.currency || 'USD',
      // RE fields persisted on fact_trade_transactions
      propertyType: formData.propertyType || null,
      propertyValuation: valuationNum,
      appraisalDate: formData.valuationDate || null,
      valuationMethod: formData.valuationMethod || null,
      loanAmount: totalDebtNum,
      netOperatingIncome: noiNum,
      annualDebtService: adsNum,
      capRate: capRatePct,
      ltv: (totalDebtNum && valuationNum) ? totalDebtNum / valuationNum : null,
      dscr: (noiNum && adsNum && adsNum > 0) ? noiNum / adsNum : null,
      debtYield: (noiNum && totalDebtNum && totalDebtNum > 0) ? noiNum / totalDebtNum : null,
      // V279 RE-specific columns
      reLocation: formData.location || null,
      reOwnershipPct: formData.ownershipPct ? parseFloat(formData.ownershipPct) : null,
      reExpectedHoldYears: formData.expectedHoldYears ? parseInt(formData.expectedHoldYears, 10) : null,
      reAppraisalFirm: formData.appraisalFirm || null,
      reOccupancyPct: formData.occupancyPct ? parseFloat(formData.occupancyPct) : null,
    };
  };

  const loadTransactionData = (transaction: any) => {
    // Trade column carries cap_rate as a percentage (V279). Older POSTED
    // trades may still hold a decimal — fall back to *100 in that case.
    const capRateRaw = transaction.capRate;
    const capRatePct = capRateRaw != null
      ? (capRateRaw < 1 ? (capRateRaw * 100).toFixed(4) : capRateRaw.toString())
      : '';
    // Strip the " (TYPE)" suffix added to the synthetic security name.
    const propertyName = (transaction.securityName || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

    // RE Equity ticket is acquisition-only at entry — but the blotter routes
    // every RE trade through this screen for read-back, including REVALUATION
    // (MTM appraisal) and SELL (DISPOSE). Derive the transactionType from the
    // loaded trade so the field reflects what actually happened on the row.
    // The select stays disabled either way; this just stops it lying.
    const loadedTransactionType = transaction.transactionType || 'BUY';

    return {
      transactionType: loadedTransactionType,
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || 'Real Estate',
      assetSubClass: transaction.assetSubClassName || 'RE Equity - Direct',
      security: transaction.securityName || '',
      identifier: transaction.cusip || transaction.isin || transaction.identifier || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      amount: transaction.amount ? transaction.amount.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      // RE equity settles on closing date — see line 87 comment.
      settleDate: transaction.settleDate || transaction.tradeDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      // RE Equity specific — sourced from fact_trade_transaction columns
      propertyName,
      propertyType: transaction.propertyType || '',
      location: transaction.reLocation || transaction.location || '',
      equityInvested: transaction.amount ? transaction.amount.toString() : '',
      ownershipPct: transaction.reOwnershipPct != null ? transaction.reOwnershipPct.toString() : '100',
      acquisitionDate: transaction.tradeDate || getTodayDate(),
      expectedHoldYears: transaction.reExpectedHoldYears != null ? transaction.reExpectedHoldYears.toString() : '',
      currentValuation: transaction.propertyValuation != null ? transaction.propertyValuation.toString() : '',
      capRate: capRatePct,
      currentNoi: transaction.netOperatingIncome != null ? transaction.netOperatingIncome.toString() : '',
      occupancyPct: transaction.reOccupancyPct != null ? transaction.reOccupancyPct.toString() : '',
      valuationDate: transaction.appraisalDate || getTodayDate(),
      valuationMethod: transaction.valuationMethod || 'CAP_RATE',
      appraisalFirm: transaction.reAppraisalFirm || '',
      totalDebt: transaction.loanAmount != null ? transaction.loanAmount.toString() : '',
      annualDebtService: transaction.annualDebtService != null ? transaction.annualDebtService.toString() : '',
    };
  };

  return (
    <BaseTicketScreen
      title="Real Estate Equity Trade Entry"
      assetClass="Real Estate"
      assetSubClass="RE Equity - Direct"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur }) => {
        // Real-time calculated metrics
        const equityInvested = formData.equityInvested ? parseAmountShorthand(formData.equityInvested) : null;
        const currentValuation = formData.currentValuation ? parseAmountShorthand(formData.currentValuation) : null;
        const currentNoi = formData.currentNoi ? parseAmountShorthand(formData.currentNoi) : null;
        const totalDebt = formData.totalDebt ? parseAmountShorthand(formData.totalDebt) : null;
        const annualDebtService = formData.annualDebtService ? parseAmountShorthand(formData.annualDebtService) : null;

        const ltv = currentValuation && totalDebt && currentValuation > 0
          ? (totalDebt / currentValuation) * 100 : null;

        const dscr = currentNoi && annualDebtService && annualDebtService > 0
          ? currentNoi / annualDebtService : null;

        const debtYield = currentNoi && totalDebt && totalDebt > 0
          ? (currentNoi / totalDebt) * 100 : null;

        const unrealizedPnl = currentValuation && equityInvested
          ? currentValuation - equityInvested : null;

        return (
          <StructuredWorkspace>
            <StructuredHeroPanel
              toneName="reEquity"
              icon={Building2}
              eyebrow="Real Estate"
              title="Direct Property Equity"
              description="Acquire property equity with ownership, valuation, operating metrics, and property-level debt context on the same ticket."
              badges={['RE Equity', 'Acquisition', 'IBOR posted']}
              signals={[
                { label: 'Property', value: formData.propertyName || 'No property', accent: !!formData.propertyName },
                { label: 'Type', value: formData.propertyType || '-' },
                { label: 'Ownership', value: formData.ownershipPct ? `${(parseFloat(formData.ownershipPct) * 100).toFixed(2)}%` : '-' },
                { label: 'Currency', value: formData.currency || 'USD' },
              ]}
            />

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="reEquity"><MapPin size={14} /> Property And Trade</BandEyebrow>
                    <BandTitle>Acquisition Setup</BandTitle>
                    <BandSubline>Property identity, ownership capital, portfolio, and closing dates.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="reEquity">BUY</ScopePill>
                </BandHeader>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="transactionType">Transaction Type*</Label>
                    <Select id="transactionType" value={formData.transactionType || 'BUY'} onChange={() => {}} disabled>
                      <option value="BUY">Acquire (Buy)</option>
                      <option value="REVALUATION">Revaluation (Appraisal)</option>
                      <option value="SELL">Dispose (Sell)</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="tradeDate">Trade Date*</Label>
                    <DateField id="tradeDate" value={formData.tradeDate ?? ''} className={errors.tradeDate ? 'error' : ''} disabled={isFieldDisabled('tradeDate')} calendars={calendarsFor('RE', formData.currency)} onChange={iso => { handleInputChange('tradeDate', iso); handleInputBlur('tradeDate', iso); }} />
                    {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="settleDate">Settle Date*</Label>
                    <DateField id="settleDate" value={formData.settleDate ?? ''} className={errors.settleDate ? 'error' : ''} disabled={isFieldDisabled('settleDate')} calendars={settlementCalendars({ ticketType: 'RE', currency: formData.currency })} onChange={iso => handleInputChange('settleDate', iso)} />
                    {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup style={{ flex: 2 }}>
                    <Label htmlFor="propertyName">Property Name*</Label>
                    <Input id="propertyName" type="text" value={formData.propertyName} onChange={(e) => {
                      handleInputChange('propertyName', e.target.value);
                      handleInputChange('security', e.target.value);
                    }} onBlur={(e) => handleInputBlur('propertyName', e.target.value)} placeholder="100 Federal Street, Boston" className={errors.propertyName ? 'error' : ''} disabled={isFieldDisabled('propertyName')} />
                    {errors.propertyName && <FieldError>{errors.propertyName}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="propertyType">Property Type*</Label>
                    <Select id="propertyType" value={formData.propertyType} onChange={(e) => handleInputChange('propertyType', e.target.value)} className={errors.propertyType ? 'error' : ''} disabled={isFieldDisabled('propertyType')}>
                      <option value="">Select Type</option>
                      <option value="OFFICE">Office</option>
                      <option value="MULTIFAMILY">Multifamily</option>
                      <option value="RETAIL">Retail</option>
                      <option value="INDUSTRIAL">Industrial</option>
                      <option value="HOTEL">Hotel</option>
                      <option value="HOSPITALITY">Hospitality</option>
                      <option value="MIXED_USE">Mixed Use</option>
                      <option value="SELF_STORAGE">Self Storage</option>
                      <option value="HEALTHCARE">Healthcare</option>
                      <option value="DATA_CENTER">Data Center</option>
                    </Select>
                    {errors.propertyType && <FieldError>{errors.propertyType}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="location">Location / Address</Label>
                    <Input id="location" type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} placeholder="Boston, MA" disabled={isFieldDisabled('location')} />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolio">Portfolio*</Label>
                    <Select id="portfolio" value={formData.portfolio} onChange={(e) => handleInputChange('portfolio', e.target.value)} onBlur={(e) => handleInputBlur('portfolio', e.target.value)} className={errors.portfolio ? 'error' : ''} disabled={isFieldDisabled('portfolio')}>
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                      ))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="equityInvested">Equity Invested ($)*</Label>
                    <Input id="equityInvested" type="text" value={formData.equityInvested} onChange={(e) => handleInputChange('equityInvested', e.target.value)} onBlur={(e) => {
                      const formatted = fmtAmount(e.target.value);
                      setFormData((prev: any) => ({ ...prev, equityInvested: formatted, amount: formatted }));
                      handleInputBlur('equityInvested', parseAmountShorthand(e.target.value).toString());
                    }} placeholder="85M" className={errors.equityInvested ? 'error' : ''} disabled={isFieldDisabled('equityInvested')} />
                    {errors.equityInvested && <FieldError>{errors.equityInvested}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="ownershipPct">Ownership % (decimal)</Label>
                    <Input id="ownershipPct" type="number" step="0.01" min="0" max="1" value={formData.ownershipPct} onChange={(e) => handleInputChange('ownershipPct', e.target.value)} placeholder="1 = 100%" disabled={isFieldDisabled('ownershipPct')} />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="currency">Currency</Label>
                    <Select id="currency" value={formData.currency} onChange={(e) => handleInputChange('currency', e.target.value)} disabled={isFieldDisabled('currency')}>
                      <option value="">Select Currency</option>
                      {dimensionData.currencies.map((c: any) => (
                        <option key={c.id} value={c.currencyCode}>{c.currencyCode} - {c.currencyName}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="acquisitionDate">Acquisition Date*</Label>
                    <DateField id="acquisitionDate" value={formData.acquisitionDate ?? ''} className={errors.acquisitionDate ? 'error' : ''} disabled={isFieldDisabled('acquisitionDate')} onChange={iso => handleInputChange('acquisitionDate', iso)} />
                    {errors.acquisitionDate && <FieldError>{errors.acquisitionDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="expectedHoldYears">Expected Hold (Years)</Label>
                    <Input id="expectedHoldYears" type="number" value={formData.expectedHoldYears} onChange={(e) => handleInputChange('expectedHoldYears', e.target.value)} placeholder="7" disabled={isFieldDisabled('expectedHoldYears')} />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                    <Select id="executedBy" value={formData.executedBy} onChange={(e) => handleInputChange('executedBy', e.target.value)} onBlur={(e) => handleInputBlur('executedBy', e.target.value)} disabled={isFieldDisabled('executedBy')} className={errors.executedBy ? 'error' : ''}>
                      <option value="">Select</option>
                      {dimensionData.users.map((user: any) => (
                        <option key={user.id} value={user.fullName}>{user.fullName}{user.role ? ` (${user.role})` : ''}</option>
                      ))}
                    </Select>
                    {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="assetManager"><Landmark size={13} /> Asset Manager*</Label>
                    <Select id="assetManager" value={formData.assetManager} onChange={(e) => handleInputChange('assetManager', e.target.value)} className={errors.assetManager ? 'error' : ''} disabled={isFieldDisabled('assetManager')}>
                      <option value="">Select Asset Manager</option>
                      {dimensionData.assetManagers.map((am: any) => (
                        <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                      ))}
                    </Select>
                    {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolioManager"><Users size={13} /> Portfolio Manager*</Label>
                    <Select id="portfolioManager" value={formData.portfolioManager} onChange={(e) => handleInputChange('portfolioManager', e.target.value)} onBlur={(e) => handleInputBlur('portfolioManager', e.target.value)} className={errors.portfolioManager ? 'error' : ''} disabled={isFieldDisabled('portfolioManager')}>
                      <option value="">Select Portfolio Manager</option>
                      {dimensionData.portfolioManagers.map((pm: any) => (
                        <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                      ))}
                    </Select>
                    {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
                  </FormGroup>
                </FormRow>
              </StructuredBand>

              <EconomicsPreview
                toneName="reEquity"
                title="Live Property Economics"
                notionalLabel="Equity Invested"
                notionalValue={equityInvested ?? 0}
                price={100}
                consideration={equityInvested ?? 0}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'Current Valuation', value: currentValuation ? `$${(currentValuation / 1e6).toFixed(1)}M` : '-' },
                  { label: 'Unrealized P&L', value: unrealizedPnl != null ? `${unrealizedPnl >= 0 ? '+' : '-'}$${(Math.abs(unrealizedPnl) / 1e6).toFixed(1)}M` : '-', accent: unrealizedPnl != null },
                  { label: 'LTV', value: pct(ltv) },
                  { label: 'DSCR', value: ratio(dscr) },
                  { label: 'Debt Yield', value: pct(debtYield, 2) },
                ]}
              />
            </TradeEconomicsGrid>

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="reEquity"><TrendingUp size={14} /> Valuation</BandEyebrow>
                  <BandTitle>Operating Metrics And Appraisal</BandTitle>
                  <BandSubline>Valuation, NOI, cap rate, occupancy, and appraisal context.</BandSubline>
                </BandTitleGroup>
              </BandHeader>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="currentValuation">Current Valuation ($)*</Label>
                  <Input id="currentValuation" type="text" value={formData.currentValuation} onChange={(e) => handleInputChange('currentValuation', e.target.value)} onBlur={(e) => {
                    const formatted = fmtAmount(e.target.value);
                    setFormData((prev: any) => ({ ...prev, currentValuation: formatted }));
                    handleInputBlur('currentValuation', parseAmountShorthand(e.target.value).toString());
                  }} placeholder="95M" className={errors.currentValuation ? 'error' : ''} disabled={isFieldDisabled('currentValuation')} />
                  {errors.currentValuation && <FieldError>{errors.currentValuation}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="capRate">Cap Rate (%)</Label>
                  <Input id="capRate" type="number" step="0.01" value={formData.capRate} onChange={(e) => handleInputChange('capRate', e.target.value)} placeholder="5.25" disabled={isFieldDisabled('capRate')} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="currentNoi">Net Operating Income ($)</Label>
                  <Input id="currentNoi" type="text" value={formData.currentNoi} onChange={(e) => handleInputChange('currentNoi', e.target.value)} onBlur={(e) => setFormData((prev: any) => ({ ...prev, currentNoi: fmtAmount(e.target.value) }))} placeholder="4.9M" disabled={isFieldDisabled('currentNoi')} />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="occupancyPct">Occupancy (%)</Label>
                  <Input id="occupancyPct" type="number" step="0.1" value={formData.occupancyPct} onChange={(e) => handleInputChange('occupancyPct', e.target.value)} placeholder="92.5" disabled={isFieldDisabled('occupancyPct')} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="valuationMethod">Valuation Method</Label>
                  <Select id="valuationMethod" value={formData.valuationMethod} onChange={(e) => handleInputChange('valuationMethod', e.target.value)} disabled={isFieldDisabled('valuationMethod')}>
                    <option value="CAP_RATE">Cap Rate</option>
                    <option value="DCF">DCF</option>
                    <option value="APPRAISAL">Appraisal</option>
                    <option value="RECONCILED">Reconciled</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="appraisalFirm">Appraisal Firm</Label>
                  <Input id="appraisalFirm" type="text" value={formData.appraisalFirm} onChange={(e) => handleInputChange('appraisalFirm', e.target.value)} placeholder="CBRE, JLL, Cushman" disabled={isFieldDisabled('appraisalFirm')} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="valuationDate">Valuation Date</Label>
                  <DateField id="valuationDate" value={formData.valuationDate ?? ''} disabled={isFieldDisabled('valuationDate')} onChange={iso => handleInputChange('valuationDate', iso)} />
                </FormGroup>
              </FormRow>
            </StructuredBand>

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="reEquity"><CalendarDays size={14} /> Property Debt</BandEyebrow>
                  <BandTitle>Debt Stack</BandTitle>
                  <BandSubline>Property-level leverage and debt service used for LTV, DSCR, and debt-yield preview.</BandSubline>
                </BandTitleGroup>
              </BandHeader>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="totalDebt">Total Debt ($)</Label>
                  <Input id="totalDebt" type="text" value={formData.totalDebt} onChange={(e) => handleInputChange('totalDebt', e.target.value)} onBlur={(e) => setFormData((prev: any) => ({ ...prev, totalDebt: fmtAmount(e.target.value) }))} placeholder="55M" disabled={isFieldDisabled('totalDebt')} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="annualDebtService">Annual Debt Service ($)</Label>
                  <Input id="annualDebtService" type="text" value={formData.annualDebtService} onChange={(e) => handleInputChange('annualDebtService', e.target.value)} onBlur={(e) => setFormData((prev: any) => ({ ...prev, annualDebtService: fmtAmount(e.target.value) }))} placeholder="2.9M" disabled={isFieldDisabled('annualDebtService')} />
                </FormGroup>
              </FormRow>
            </StructuredBand>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default RealEstateEquityTicketScreen;
